import { useCallback, useState } from "react";
import { ArrowLeft, CreditCard, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { HttpRequestError } from "@workspace/base-api/fetchJson";
import { Button } from "@workspace/ui/components/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@workspace/ui/components/card";
import {
	PIN_REJECTED_MESSAGE,
	pinFailureMessage,
	StaffLoginForm,
	StaffPinResetForm,
	staffPortalApi,
	thrownFailureMessage,
	useStaffPortalContext,
	type StaffMember,
} from "@workspace/venue-management-staff";
import {
	activateEmployeeCentreSession,
	EMPLOYEE_CENTRE_SESSION_ENDED_MESSAGE,
	leaveEmployeeCentre,
	useEmployeeCentreSessionEnded,
} from "@workspace/venue-management-staff-terminal/employee-centre";

import { CoreDetailsSection } from "./-components/core-details-section";
import { MyShiftsSection } from "./-components/my-shifts-section";

export const Route = createFileRoute(
	"/_auth/_dashboard/$tenant/venues/$venueId/employee-centre",
)({
	component: RouteComponent,
});

/** HA-649 Slice 0: the server's refusal, said as the server says it (a spent PIN check, a role without the key); otherwise a fallback. */
function startFailureMessage(error: unknown) {
	if (
		error instanceof HttpRequestError &&
		error.response.status === 403 &&
		error.problem?.detail
	)
		return error.problem.detail;
	return thrownFailureMessage(
		error,
		"We couldn't sign you in. Please try again.",
	);
}

function RouteComponent() {
	const nav = Route.useNavigate();
	const { tenant, venueId } = Route.useRouteContext();
	const { data, todayShift } = useStaffPortalContext();

	const [authenticatedStaff, setAuthenticatedStaff] =
		useState<StaffMember | null>(null);
	// HA-829: the PIN check's single-use ticket, for a temporary PIN's replacement.
	const [ticket, setTicket] = useState<string | null>(null);
	const [status, setStatus] = useState<
		"login" | "pin-reset-required" | "authenticated"
	>("login");

	const checkPin = useMutation({
		...staffPortalApi.checkPinOptions(tenant, venueId),
	});

	const setPin = useMutation({
		...staffPortalApi.setPinOptions(tenant, venueId),
	});

	function navigateToDashboard() {
		nav({
			to: "/$tenant/venues/$venueId",
			params: { tenant, venueId },
		});
	}

	function backToLogin() {
		setAuthenticatedStaff(null);
		setTicket(null);
		setStatus("login");
	}

	// HA-649 Slice 0: Logout and Back end the session at the server.
	async function leave() {
		backToLogin();
		await leaveEmployeeCentre();
		navigateToDashboard();
	}

	// HA-649 Slice 0: the server no longer accepts the session (ended, or its 15 minutes are up): sign in again.
	useEmployeeCentreSessionEnded(
		useCallback(() => {
			backToLogin();
			toast.error(EMPLOYEE_CENTRE_SESSION_ENDED_MESSAGE);
		}, []),
	);

	async function handleLogin(pinData: { staffId: string; pin: string }) {
		const staff = data.staffMembers.find(
			(s) => s.staffNumber === parseInt(pinData.staffId, 10),
		);

		if (!staff) {
			toast.error(PIN_REJECTED_MESSAGE);
			return false;
		}

		const result = await checkPin.mutateAsync({
			registeredUserId: staff.registeredUserId,
			pin: pinData.pin,
		});

		if (!result.success) {
			// HA-804: only the server's 401 is a wrong PIN; a 403, a 500 or a dropped connection
			// says what it is.
			toast.error(pinFailureMessage(result));
			return false;
		}

		if (result.data.isTemporary) {
			setAuthenticatedStaff(staff);
			setTicket(result.data.ticket);
			setStatus("pin-reset-required");
			return false;
		}

		// HA-649 Slice 0: the PIN's ticket starts this staff member's own Employee Centre session. The server decides whether their
		// role may open it (a 403 says so); the browser no longer checks.
		try {
			const started = await staffPortalApi.startEmployeeCentreSession(
				tenant,
				venueId,
				{ staffMemberId: staff.id, ticket: result.data.ticket },
			);
			activateEmployeeCentreSession(started, tenant, venueId);
		} catch (error) {
			toast.error(startFailureMessage(error));
			return false;
		}

		setAuthenticatedStaff(staff);
		setTicket(null);
		setStatus("authenticated");
		return true;
	}

	async function handlePinReset(resetData: {
		newPin: string;
		confirmPin: string;
	}) {
		if (!authenticatedStaff || !ticket) {
			toast.error("Please enter your PIN again");
			return false;
		}

		const response = await setPin.mutateAsync({
			ticket,
			staffMemberId: authenticatedStaff.id,
			pin: resetData.newPin,
		});

		if (response.success) {
			toast.success("PIN reset successfully! Please log in again.");
			backToLogin();
			return true;
		}

		toast.error("Failed to reset PIN. Please try again.");
		return false;
	}

	if (status === "login") {
		return (
			<div className="flex min-h-(--kiosk-vh,100vh) flex-col items-center justify-center p-4">
				<Card className="w-full max-w-md">
					<CardHeader className="text-center">
						<CardTitle className="text-2xl">Employee Centre</CardTitle>
						<p className="text-sm text-muted-foreground">
							Enter your Staff ID and PIN to access your profile
						</p>
					</CardHeader>
					<CardContent>
						<StaffLoginForm
							onSubmit={handleLogin}
							onCancel={navigateToDashboard}
						/>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (status === "pin-reset-required") {
		return (
			<div className="flex min-h-(--kiosk-vh,100vh) flex-col items-center justify-center p-4">
				<Card className="w-full max-w-md">
					<CardHeader className="text-center">
						<CardTitle className="text-2xl">Reset Your PIN</CardTitle>
						<p className="text-sm text-muted-foreground">
							Your PIN is temporary. Please set a new PIN to continue.
						</p>
					</CardHeader>
					<CardContent>
						<StaffPinResetForm
							onSubmit={handlePinReset}
							onCancel={backToLogin}
						/>
					</CardContent>
				</Card>
			</div>
		);
	}

	const staffShifts = todayShift.filter(
		(shift) => shift.staffMemberId === authenticatedStaff?.id,
	);

	return (
		<div className="flex min-h-(--kiosk-vh,100vh) flex-col p-4">
			<div className="mb-4 flex items-center justify-between">
				<Button variant="outline" size="sm" onClick={() => void leave()}>
					<ArrowLeft className="mr-1 size-4" />
					Back
				</Button>
				<h1 className="text-xl font-bold">Employee Centre</h1>
				<Button variant="outline" size="sm" onClick={() => void leave()}>
					<LogOut className="mr-1 size-4" />
					Logout
				</Button>
			</div>

			<div className="mx-auto w-full max-w-2xl space-y-4">
				<CoreDetailsSection tenant={tenant} venueId={venueId} />
				<MyShiftsSection shifts={staffShifts} />

				<Card className="opacity-60">
					<CardContent className="flex flex-col items-center justify-center p-6">
						<div className="mb-2 text-muted-foreground">
							<CreditCard className="size-5" />
						</div>
						<span className="text-sm font-medium">My Pay</span>
						<span className="text-xs text-muted-foreground">Coming soon</span>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
